// Lobby-mounted live leaderboard billboard. Shows the top-3 7-day
// global standings on a screen positioned in 3D world space between
// the two north plaza subrooms. The screen content is a DOM overlay
// anchored to a fixed world point (projected to screen each frame —
// same pattern as the attack-ring overlay) sitting in front of a 3D
// "stadium scoreboard" structure: 2 metal poles, dark frame, yellow
// trim with corner bulbs, and a STANDINGS banner above. Click the
// screen to open the full leaderboard modal.
//
// Polls the leaderboard endpoint every LEADERBOARD_POLL_MS while the
// player is in the lobby; pauses while the player is in a match so
// we don't burn requests.

import * as THREE from 'three';
import type { FollowCamera } from '../scene/camera';
import { fetchGlobalLeaderboard, type GlobalRow } from './leaderboard_api';

const LEADERBOARD_POLL_MS = 60_000;
// World-space anchor — between the two north plaza subrooms (NW
// matchmaker / NE leaderboards). The screen sits at SCREEN_Y; the
// poles climb from ground (y=0) to SCREEN_Y - SCREEN_H/2 so the
// structure reads as a unified scoreboard mounted on twin posts.
const BILLBOARD_X = 0;
const BILLBOARD_Z = -10.2;
// Screen content size — the actual clickable DOM rectangle. Sized
// tight to the rendered content (3 rows + CLICK TO OPEN footer) so
// the leaderboard reads as a focused readout, not a half-empty TV.
const BILLBOARD_WORLD_W = 4.6;
const BILLBOARD_WORLD_H = 2.4;
// 3D scoreboard frame size — the chunky black surround behind the
// screen. Bumped 2026-05-01 (+32% W / +35% H) so the structure reads
// as a proper stadium scoreboard rather than a small wall sign; the
// inner screen size is unchanged so leaderboard text stays readable.
const FRAME_W = 7.5;
const FRAME_H = 5.0;
// Screen anchor — bumped to 4.2 (was 3.6) so the taller frame still
// has visible poles below it instead of sinking into the floor.
const SCREEN_Y = 4.2;
export const BILLBOARD_WORLD_POS = new THREE.Vector3(BILLBOARD_X, SCREEN_Y, BILLBOARD_Z);

export type LobbyLeaderboardBillboard = {
  /** Project world pos → screen pos this frame. Call from render loop. */
  update(camera: FollowCamera): void;
  /** Show the billboard (lobby visible). */
  setVisible(v: boolean): void;
  /** Force a top-3 refetch. Call after match-end to reflect new scores. */
  refresh(): void;
  /** Tear down DOM + timer + 3D structure. */
  destroy(): void;
};

// 3D "stadium scoreboard" structure — 2 thick metal poles, concrete
// pads, mount brackets, a chunky dark frame substantially larger than
// the screen, yellow trim, corner bulbs, and a STANDINGS banner header
// integrated into the frame. Designed to read as a single fabricated
// unit, not a screen suspended in the air. Returns a Group so the
// caller can hide / dispose it.
function buildScoreboardStructure(): THREE.Group {
  const g = new THREE.Group();
  g.position.set(BILLBOARD_X, 0, BILLBOARD_Z);

  // --- Proportions ---
  // Frame W/H come from the module-level FRAME_W/FRAME_H constants
  // and are intentionally MUCH larger than the inner screen so the
  // screen reads as an inset display panel inside a chunky surround
  // (not a screen pasted onto the front of a flat plane).
  const FRAME_DEPTH = 0.45;                       // chunky bezel
  // Header band at top of frame for the STANDINGS title — distinct
  // strip rather than a separate floating banner so it reads as part
  // of the same fabricated unit.
  const HEADER_H = 0.95;
  // Poles span almost the full frame width so the whole assembly
  // reads as one piece. Bottom of frame sits on top of the poles.
  const POLE_OUT = FRAME_W / 2 - 0.6;             // ≈ 4.4
  const POLE_TOP = SCREEN_Y - FRAME_H / 2;        // ≈ 1.9
  const POLE_RAD_TOP = 0.28;
  const POLE_RAD_BOTTOM = 0.42;
  const PAD_RAD_TOP = 0.62;
  const PAD_RAD_BOTTOM = 0.82;
  const PAD_H = 0.22;
  const SIGN_Z = 0;

  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x4b5563, roughness: 0.4, metalness: 0.75,
  });
  const padMat = new THREE.MeshStandardMaterial({
    color: 0x9ca3af, roughness: 0.85, metalness: 0.15,
  });
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x0f172a, roughness: 0.55, metalness: 0.35,
  });
  const innerBezelMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b, roughness: 0.6, metalness: 0.25,
  });
  const headerMat = new THREE.MeshStandardMaterial({
    color: 0x111827, roughness: 0.4, metalness: 0.5,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0xfbbf24,
    emissive: 0xfbbf24,
    emissiveIntensity: 0.65,
    roughness: 0.3,
    metalness: 0.4,
  });
  const bulbMat = new THREE.MeshStandardMaterial({
    color: 0xfde047,
    emissive: 0xfde047,
    emissiveIntensity: 1.5,
    roughness: 0.2,
  });
  const bracketMat = new THREE.MeshStandardMaterial({
    color: 0x374151, roughness: 0.45, metalness: 0.7,
  });

  // --- 2 thick vertical poles + concrete pads at the base ---
  for (const dx of [-POLE_OUT, POLE_OUT]) {
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(POLE_RAD_TOP, POLE_RAD_BOTTOM, POLE_TOP, 14),
      poleMat,
    );
    pole.position.set(dx, POLE_TOP / 2, 0.05);
    g.add(pole);
    const pad = new THREE.Mesh(
      new THREE.CylinderGeometry(PAD_RAD_TOP, PAD_RAD_BOTTOM, PAD_H, 18),
      padMat,
    );
    pad.position.set(dx, PAD_H / 2, 0.05);
    g.add(pad);
    // Bracket where each pole meets the underside of the frame —
    // tapered cube that visually mounts the frame onto the pole.
    const bracket = new THREE.Mesh(
      new THREE.BoxGeometry(0.55, 0.32, 0.55),
      bracketMat,
    );
    bracket.position.set(dx, POLE_TOP + 0.16, 0.05);
    g.add(bracket);
  }

  // --- Dark backdrop frame (the chunky bezel) ---
  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(FRAME_W, FRAME_H, FRAME_DEPTH),
    frameMat,
  );
  frame.position.set(0, SCREEN_Y, SIGN_Z);
  g.add(frame);

  // Inner bezel — slightly recessed lighter panel that the screen
  // appears to "sit inside". Adds depth instead of a flat block.
  const innerBezel = new THREE.Mesh(
    new THREE.BoxGeometry(BILLBOARD_WORLD_W + 0.45, BILLBOARD_WORLD_H + 0.45, FRAME_DEPTH * 0.5),
    innerBezelMat,
  );
  innerBezel.position.set(0, SCREEN_Y - HEADER_H * 0.18, SIGN_Z + FRAME_DEPTH * 0.32);
  g.add(innerBezel);

  // --- Header band integrated into the top of the frame ---
  const header = new THREE.Mesh(
    new THREE.BoxGeometry(FRAME_W - 0.25, HEADER_H, FRAME_DEPTH * 0.6),
    headerMat,
  );
  header.position.set(0, SCREEN_Y + FRAME_H / 2 - HEADER_H / 2 - 0.05, SIGN_Z + FRAME_DEPTH * 0.35);
  g.add(header);

  const headerLabel = makeBannerMesh('★ STANDINGS ★', FRAME_W - 0.6, HEADER_H * 0.78);
  headerLabel.position.set(
    0,
    SCREEN_Y + FRAME_H / 2 - HEADER_H / 2 - 0.05,
    SIGN_Z + FRAME_DEPTH * 0.61,
  );
  g.add(headerLabel);

  // --- Yellow trim — full perimeter rectangle of strips ---
  const trimW = FRAME_W + 0.18;
  const trimT = 0.22;
  const trimDepth = FRAME_DEPTH + 0.05;
  for (const dy of [-FRAME_H / 2, FRAME_H / 2]) {
    const trim = new THREE.Mesh(
      new THREE.BoxGeometry(trimW, trimT, trimDepth),
      trimMat,
    );
    trim.position.set(0, SCREEN_Y + dy, SIGN_Z);
    g.add(trim);
  }
  for (const dx of [-FRAME_W / 2, FRAME_W / 2]) {
    const trim = new THREE.Mesh(
      new THREE.BoxGeometry(trimT, FRAME_H, trimDepth),
      trimMat,
    );
    trim.position.set(dx, SCREEN_Y, SIGN_Z);
    g.add(trim);
  }
  // Inner divider strip between header and screen so the two zones
  // read as separate panels of the same fabricated unit.
  const divider = new THREE.Mesh(
    new THREE.BoxGeometry(FRAME_W - 0.25, 0.1, FRAME_DEPTH * 0.7),
    trimMat,
  );
  divider.position.set(
    0,
    SCREEN_Y + FRAME_H / 2 - HEADER_H - 0.05,
    SIGN_Z + FRAME_DEPTH * 0.4,
  );
  g.add(divider);

  // --- 4 stadium bulbs at corners + 2 mid-edge lights for that real
  // scoreboard look ---
  const bulbR = 0.18;
  for (const cx of [-1, 1]) {
    for (const cy of [-1, 1]) {
      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(bulbR, 12, 10),
        bulbMat,
      );
      bulb.position.set(
        cx * (FRAME_W / 2 + 0.16),
        SCREEN_Y + cy * (FRAME_H / 2 + 0.16),
        SIGN_Z + 0.12,
      );
      g.add(bulb);
    }
  }
  // 2 extra bulbs along the top edge (between the corners) for the
  // "stadium row of lights" feel.
  for (const cx of [-0.4, 0.4]) {
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(bulbR * 0.85, 12, 10),
      bulbMat,
    );
    bulb.position.set(cx * FRAME_W, SCREEN_Y + FRAME_H / 2 + 0.16, SIGN_Z + 0.12);
    g.add(bulb);
  }

  return g;
}

function makeBannerMesh(text: string, w: number, h: number): THREE.Mesh {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 96;
  const ctx = c.getContext('2d');
  if (ctx) {
    // Dark navy panel with a yellow accent border, text in bright gold.
    ctx.fillStyle = '#0b1220';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, c.width - 6, c.height - 6);
    ctx.fillStyle = '#fbbf24';
    ctx.font = '900 56px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, c.width / 2, c.height / 2 + 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  return new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
}

export function mountLobbyLeaderboardBillboard(
  parent: HTMLElement,
  scene3D: THREE.Object3D,
  onOpen: () => void,
): LobbyLeaderboardBillboard {
  // 3D structure — added to the lobbyGroup so it auto-hides on match.
  const structure = buildScoreboardStructure();
  scene3D.add(structure);

  const root = document.createElement('button');
  root.className = 'ui interactive smp-lobby-lb';
  root.type = 'button';
  root.setAttribute('aria-label', 'Open leaderboards');
  // Transparent button — the 3D scoreboard bezel + yellow trim + corner
  // bulbs already provide all the framing. The DOM overlay's job is
  // just to render the live leaderboard CONTENT (rows + footer) on top
  // of the dark inner bezel. Any DOM border/shadow/background here
  // would visually double up the frame and read as two stacked boxes.
  root.style.cssText = `
    position: fixed;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    color: #e5e7eb;
    font-family: system-ui, sans-serif;
    cursor: pointer;
    overflow: hidden;
    pointer-events: auto;
    z-index: 30;
    transition: filter .14s ease;
    display: none;
  `;
  root.addEventListener('mouseenter', () => {
    // Cheap hover affordance: brighten the text without adding any
    // outline/shadow that would re-introduce a DOM border.
    root.style.filter = 'brightness(1.18)';
  });
  root.addEventListener('mouseleave', () => {
    root.style.filter = '';
  });
  root.addEventListener('click', () => onOpen());
  parent.appendChild(root);

  let topList: GlobalRow[] = [];
  let fetchInFlight = false;
  let isVisible = false;
  let lastWidthPx = 0;

  const render = (widthPx: number): void => {
    // Scale internal font sizes proportionally to the rendered width
    // so the billboard reads the same regardless of camera zoom.
    // Multipliers tuned for the now-smaller screen so rows still read
    // clearly inside a tight inner panel.
    const rowFs = Math.max(10, Math.round(widthPx * 0.085));
    const footerFs = Math.max(8, Math.round(widthPx * 0.065));
    const padPx = Math.max(3, Math.round(widthPx * 0.022));
    const rowsHtml = topList.length
      ? topList.map((r) => renderRow(r, rowFs)).join('')
      : `<div style="text-align:center;opacity:.6;font:700 ${rowFs}px ui-monospace;padding:${rowFs}px 0;">loading…</div>`;
    // Pure content — the 3D scoreboard structure provides the header
    // (★ STANDINGS ★ banner), the bezel, and the trim. The DOM screen
    // shows only the live rows + the click-to-open footer so the two
    // layers read as a single fabricated unit.
    root.innerHTML = `
      <div style="
        height:100%;
        display:flex;flex-direction:column;justify-content:space-between;
        padding:${padPx}px ${padPx * 1.4}px;
      ">
        <div>${rowsHtml}</div>
        <div style="
          text-align:center;
          font:800 ${footerFs}px system-ui;letter-spacing:1.5px;
          color:rgba(251,191,36,.85);
          padding-top:${padPx * 0.4}px;
        ">CLICK TO OPEN →</div>
      </div>
    `;
  };

  const update = (camera: FollowCamera): void => {
    if (!isVisible) return;
    const cam = camera.camera;
    const v = _scratch.copy(BILLBOARD_WORLD_POS).project(cam);
    // Off-screen → hide cheaply (avoid drawing rotated negative-coord
    // overlays that paint outside the viewport).
    if (v.z > 1 || v.z < -1) { root.style.opacity = '0'; return; }
    const sx = (v.x + 1) * 0.5 * window.innerWidth;
    const sy = (1 - v.y) * 0.5 * window.innerHeight;
    const orthoH = cam.top - cam.bottom;
    const pxPerUnit = window.innerHeight / Math.max(0.0001, orthoH);
    const widthPx = BILLBOARD_WORLD_W * pxPerUnit;
    const heightPx = BILLBOARD_WORLD_H * pxPerUnit;
    root.style.left = `${Math.round(sx - widthPx / 2)}px`;
    root.style.top = `${Math.round(sy - heightPx / 2)}px`;
    root.style.width = `${Math.round(widthPx)}px`;
    root.style.height = `${Math.round(heightPx)}px`;
    root.style.opacity = '1';
    // Re-render only when the rendered width changes by ≥4px so font
    // sizes stay readable across zoom changes without thrashing innerHTML
    // every frame.
    if (Math.abs(widthPx - lastWidthPx) > 4) {
      lastWidthPx = widthPx;
      render(widthPx);
    }
  };

  const setVisible = (v: boolean): void => {
    if (v === isVisible) return;
    isVisible = v;
    root.style.display = v ? 'block' : 'none';
    if (v) {
      void poll();
      pollTimer = window.setInterval(() => void poll(), LEADERBOARD_POLL_MS);
    } else if (pollTimer != null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  let pollTimer: number | null = null;
  const poll = async (): Promise<void> => {
    if (fetchInFlight) return;
    fetchInFlight = true;
    try {
      const lb = await fetchGlobalLeaderboard();
      topList = lb.entries.slice(0, 3);
      render(lastWidthPx || 320);
    } catch { /* ignore — keep stale */ }
    finally { fetchInFlight = false; }
  };

  const refresh = (): void => { void poll(); };

  const destroy = (): void => {
    if (pollTimer != null) { clearInterval(pollTimer); pollTimer = null; }
    root.remove();
    structure.parent?.remove(structure);
    structure.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const m = obj.material;
        if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
        else m.dispose();
      }
    });
  };

  // Initial render so the billboard reads "loading…" before the first
  // fetch resolves; size will update on first `update()` call.
  render(320);

  return { update, setVisible, refresh, destroy };
}

const _scratch = new THREE.Vector3();

function renderRow(r: GlobalRow, fs: number): string {
  const color = r.role === 'int' ? '#60a5fa' : '#fb923c';
  const nameSafe = escapeHtml(r.name.slice(0, 14));
  return `
    <div style="
      display:flex;align-items:center;gap:${Math.round(fs * 0.6)}px;
      padding:${Math.round(fs * 0.25)}px 0;
      font:800 ${fs}px ui-monospace, system-ui;
      border-bottom:1px solid rgba(255,255,255,.06);
    ">
      <span style="
        color:#fbbf24;min-width:${Math.round(fs * 1.6)}px;
        text-align:right;
      ">#${r.rank}</span>
      <span style="
        color:${color};flex:1;overflow:hidden;
        text-overflow:ellipsis;white-space:nowrap;
      ">${nameSafe}</span>
      <span style="color:#e5e7eb;">${r.kills}k</span>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
